import { default as React, FC, Fragment, useEffect, useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import routePaths from 'config/routes';
import Router from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import redirectTo from 'services/redirectTo';
import CustomModal from 'src/components/CustomModal';
import { setFooterHeight } from 'src/actions/appActions';
import { UpdateUserPoints } from 'src/actions/authActions';
import CheckoutDonationsDetails from 'src/components/experienceCheckout/CheckoutDonationDetails';
import BasicHeader from 'src/components/layout/BasicHeader';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import { ResourceTypeEnum } from 'src/shared/enums';
import {
  IChallenge,
  IChallengeCheckoutDonation,
  IDonationRequest,
} from 'src/shared/models';
import DonationSelector from 'src/components/DonationSelector';
import {
  checkUserResourcePoints,
  completeDonationCheckout,
  customFormatMessage,
  scrollDown,
} from 'utils';
interface OwnProps {
  config: IChallengeCheckoutDonation;
  loading: boolean;
  error: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  showModal: boolean;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number,
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    userPoints: state.auth.userPoints,
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setFooterHeight }, dispatch);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const CheckoutDonation: FC<Props> = ({
  challenge,
  config,
  loading,
  error,
  setLoading,
  setShowModal,
  showModal,
  setError,
  updateChallenge,
  updateUserPoints,
  userPoints,
  setFooterHeight,
  user,
}) => {
  const { formatMessage } = useIntl();
  const { donations, points } = config;

  const showCheckoutButton = !showModal && config.isChallengeInDate;
  const pointsLabel = Math.abs(points);

  const [selectedDonation, setSelectedDonation] = useState<number>(pointsLabel);

  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    selectedDonation,
    challenge?.challengeType,
  );

  const donationSelector = (
    <DonationSelector
      donations={donations}
      selectedDonation={selectedDonation}
      setSelectedDonation={setSelectedDonation}
    />
  );

  useEffect(() => {
    if (showCheckoutButton) {
      setFooterHeight('checkout');
    }
    return () => {
      setFooterHeight(null);
    };
  }, [showModal, config.isChallengeInDate]);

  const performCheckout = async () => {
    setLoading(true);

    const data: IDonationRequest = {
      id: config.idResource,
      points: selectedDonation,
    };

    const err = await completeDonationCheckout({
      data,
      idResource: config.idResource,
      updateChallenge,
      updateUserPoints,
    });

    setLoading(false);

    if (err) {
      const stringError: string = formatMessage({
        id: 'experience.error-checkout',
      });
      setError(stringError);
      return;
    }

    redirectTo(routePaths.PAGES.DONATION_CHECKOUT_END, undefined, Router);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollTest = () => {
    const testResource = challenge.challengeResources.find(
      (item) => item.type === ResourceTypeEnum.QUIZ_TYPEFORM,
    );
    if (testResource) {
      scrollDown(testResource.key);
    }
  };

  const checkoutDonationModal = () => {
    const checkoutError = (
      <CustomModal
        content={error}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setError(null);
        }}
        showIcon={false}
        visible={Boolean(showModal && error)}
      />
    );

    return (
      <Modal
        visible={showModal}
        className="checkout-modal"
        footer={null}
        mask={false}
        maskTransitionName={''}
        transitionName={''}
        onCancel={() => setShowModal(false)}>
        <BasicHeader />

        <div className="container checkout-modal__col">
          {/** LEFT COLUMN */}
          <div className="checkout-modal__content">
            <div className="ant-row ant-row-left checkout-modal-details_back">
              <div className="donation-selector__container">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-return">
                  <LeftOutlined />
                  {formatMessage({ id: 'page.experience.checkout.back' })}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container checkout-modal__col">
          {/** LEFT COLUMN */}
          <div className="checkout-modal__left-column checkout-modal__content">
            <div className="ant-row ant-row-center checkout-modal-details">
              <div className="donation-selector__container">
                <div className="donation-selector__lead donation-selector__lead-product">
                  {formatMessage({
                    id: 'page.experience.donation.checkout.title',
                  })}
                </div>
                <div className="ant-radio-group ant-radio-group-outline ant-radio-group-large donation-item product-selector__wrapper">
                  {donationSelector}
                </div>
              </div>
            </div>
          </div>

          {/** RIGHT COLUMN */}
          <div className="checkout-modal__right-column">
            <div className="checkout-modal__right-column--container">
              <div className="checkout-modal-details">
                <CheckoutDonationsDetails
                  challengeType={challenge?.challengeType}
                  checkoutPoints={selectedDonation}
                  image={config.image}
                  performCheckout={performCheckout}
                  termsLabel={config.termsLabel}
                  termsUrl={config.termsUrl}
                  text={config.text}
                  title={config.title}
                  userPoints={userPoints}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>
        {checkoutError}
      </Modal>
    );
  };

  const checkoutDonationButtonBalance = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    if (config.isBlocked) {
      return (
        <div className="experience-checkout-button__points experience-checkout-lottery-button">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              <Button
                className="btn-ghost--minimalist experience-checkout-lottery-button__link"
                onClick={handleScrollTest}>
                {customFormatMessage(
                  'page.experience.checkout.test-dependency',
                )}
              </Button>
            </p>
          </div>
        </div>
      );
    }

    if (!hasUserEnoughPoints) {
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {customFormatMessage(
                'page.experience.checkout.user-balance-lacks-{points}',
                {
                  points: Number(pointsLabel - userPoints),
                },
              )}
            </p>
          </div>

          <div className="experience-checkout-button__points-link">
            <Link href={routePaths.PAGES.SCAN.SCAN_INDEX}>
              <Button className="btn-line">
                {formatMessage({
                  id: 'page.experience.checkout.user-balance-get-more-points',
                })}
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage(
              'page.experience.checkout.user-balance-has-points',
            )}
          </p>
        </div>
      </div>
    );
  };

  const buttonContent = (
    <>
      {checkoutDonationButtonBalance()}
      <Button
        disabled={config.isBlocked || !hasUserEnoughPoints || user.blacklisted}
        loading={loading}
        className="btn-primary btn-primary--black experience-checkout-button experience-checkout-lottery-button__button"
        onClick={() => setShowModal(true)}>
        <div className="experience-checkout-button__container">
          {formatMessage({
            id: 'page.experience.checkout.donation-collaborate-button',
          })}
        </div>
      </Button>
    </>
  );

  const checkoutButton = (
    <>
      <div className="show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow experience-checkout-lottery-button">
        {buttonContent}
      </div>
      <div className="not_show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div>
            <img
              className="experience-checkout-button__container--image"
              src={config.image}
              alt="checkout-img"
            />
            <div className="experience-checkout-button__container--title">
              <span>{config.title}</span>
            </div>
          </div>
          <div>{buttonContent}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {showCheckoutButton && checkoutButton}
      {checkoutDonationModal()}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutDonation);
